import { readDeviceSettings } from './settings'

export type FeedbackKind = 'accepted' | 'queued' | 'rejected'

interface Tone {
  frequency: number
  durationMs: number
  gapMs: number
}

const PATTERNS: Record<FeedbackKind, { tones: Tone[]; vibration: number | number[] }> = {
  accepted: {
    // A short upward two-note chime means the local action was accepted.
    tones: [
      { frequency: 880, durationMs: 55, gapMs: 8 },
      { frequency: 1320, durationMs: 75, gapMs: 0 },
    ],
    vibration: 18,
  },
  queued: {
    // Two low, even notes are deliberately unlike the accepted upward chime.
    tones: [
      { frequency: 440, durationMs: 75, gapMs: 65 },
      { frequency: 440, durationMs: 75, gapMs: 0 },
    ],
    vibration: [24, 45, 24],
  },
  rejected: {
    // A falling pair is the error signature, with a longer tactile warning.
    tones: [
      { frequency: 700, durationMs: 90, gapMs: 12 },
      { frequency: 220, durationMs: 145, gapMs: 0 },
    ],
    vibration: [70, 35, 70],
  },
}

let audioContext: AudioContext | null = null

/**
 * Feedback is intentionally fire-and-forget. A blocked AudioContext, missing vibrate
 * API, or IndexedDB read must never delay or fail the scan write.
 */
export function triggerFeedback(kind: FeedbackKind): void {
  void readDeviceSettings()
    .then((settings) => {
      if (settings.sound) {
        playTonePattern(PATTERNS[kind].tones)
      }

      if (settings.haptics) {
        vibrate(PATTERNS[kind].vibration)
      }
    })
    .catch(() => undefined)
}

function playTonePattern(tones: Tone[]): void {
  const context = getAudioContext()

  if (context === null) {
    return
  }

  const start = () => {
    let offset = context.currentTime

    for (const tone of tones) {
      const oscillator = context.createOscillator()
      const gain = context.createGain()
      const startAt = offset
      const endAt = startAt + tone.durationMs / 1000

      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(tone.frequency, startAt)
      gain.gain.setValueAtTime(0.0001, startAt)
      gain.gain.exponentialRampToValueAtTime(0.12, startAt + 0.008)
      gain.gain.exponentialRampToValueAtTime(0.0001, endAt)
      oscillator.connect(gain)
      gain.connect(context.destination)
      oscillator.start(startAt)
      oscillator.stop(endAt)
      offset = endAt + tone.gapMs / 1000
    }
  }

  if (context.state === 'suspended') {
    void context.resume().then(start).catch(() => undefined)
    return
  }

  try {
    start()
  } catch {
    // Audio is an enhancement. Some handheld browsers expose AudioContext but reject
    // oscillator creation until a user gesture.
  }
}

function getAudioContext(): AudioContext | null {
  if (audioContext !== null) {
    return audioContext
  }

  try {
    if (typeof AudioContext === 'undefined') {
      return null
    }

    audioContext = new AudioContext()
    return audioContext
  } catch {
    return null
  }
}

function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern)
  } catch {
    // iOS Safari and some managed handheld browsers do not expose vibration.
  }
}
